'use client';
import React, { useState } from 'react';
import { Carousel, CarouselContent, CarouselItem, CarouselApi } from '../ui/carousel';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Button } from '../ui/button';
import StoreCard from '@/app/[locale]/stores/components/store-card';
import { IStore } from '@/types/stores';
import { useLocale, useTranslations } from 'next-intl';
import { Link } from '@/i18n/routing';
import { Routes } from '@/lib/routes/routes';
import CurrentLanguageText from '../helper/CurrentLanguageText';
import Typography from '../ui/typography';

export type TStores = {
  Id: number;
  Name: string;
  Store: {
    Id: number;
    Name: string;
    LogoURL: string;
  };
  Distance: number;
  IsOpen: boolean;
}[];

type CarouselSection = {
  slides: IStore[];
  currentLang: 'ar' | 'en';
  title?: string;
  englishName: string;
  arabicName: string;
};

type CarouselNavigationProps = {
  onPrev: () => void;
  onNext: () => void;
};

const CarouselNavigation: React.FC<CarouselNavigationProps> = ({ onPrev, onNext }) => (
  <div className="mx-4 mb-2 flex justify-end gap-3 rtl:flex-row-reverse">
    <button
      className="bg-secondary grid h-9 w-9 place-items-center overflow-hidden rounded-full lg:h-11 lg:w-11"
      onClick={onPrev}
      aria-label="Scroll to previous item"
    >
      <ChevronLeftIcon className="text-primary" size={26} />
    </button>
    <button
      className="bg-secondary grid h-9 w-9 place-items-center overflow-hidden rounded-full lg:h-11 lg:w-11"
      onClick={onNext}
      aria-label="Scroll to next item"
    >
      <ChevronRightIcon className="text-primary" size={26} />
    </button>
  </div>
);

function StoresCarousel(props: CarouselSection) {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const t = useTranslations('stores');
  const locale = useLocale();
  return (
    <div className="h-full w-full flex-col lg:flex">
      <Carousel
        setApi={setCarouselApi}
        opts={{
          slidesToScroll: 'auto',
          dragFree: true,
          direction: locale === 'ar' ? 'rtl' : 'ltr',
        }}
        className="m-auto w-full self-center lg:h-full"
      >
        <div className="flex justify-between">
          <div className="flex flex-col items-start pl-5">
            {props.title && <h2 className="mb-0 text-2xl font-semibold">{props.title}</h2>}

            <Typography className="mb-2" variant={'h4'}>
              <CurrentLanguageText arabicName={props.arabicName} englishName={props.englishName} />
            </Typography>
          </div>
          <div className="flex flex-row-reverse justify-between">
            <CarouselNavigation
              onPrev={() => carouselApi?.scrollPrev()}
              onNext={() => carouselApi?.scrollNext()}
            />
          </div>
        </div>

        <CarouselContent className="mx-2 flex touch-pan-y gap-2.5 py-3">
          {props.slides?.map((data) => (
            <CarouselItem
              key={data.id}
              className="xs:flex-[0_0_80%] pl-0 md:flex-[0_0_50%] lg:flex-[0_0_33%]"
              dir={props.currentLang === 'ar' ? 'rtl' : 'ltr'}
            >
              <StoreCard storeContent={data} key={data.id} />
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="mx-auto mt-6 w-full md:w-56">
          <Link href={Routes.stores}>
            <Button variant={'secondary'} className="py-5" rounded-sm={'full'}>
              {t('Explore More')}
            </Button>
          </Link>
        </div>
      </Carousel>
    </div>
  );
}

export default StoresCarousel;
